/**
 * Truncate — CSS line clamp, plus a tooltip *only when the text is actually cut*.
 *
 * An always-on `title` is noise: it fires on text that is fully visible and
 * teaches users to ignore it. This measures the node and attaches the title only
 * while it overflows, re-checking whenever the box resizes.
 */

import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';

export interface TruncateProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'title'> {
  /** 1 → ellipsised single line. 2+ → clamped block. */
  lines?: number;
  /** Overrides the measured text used for the tooltip. */
  title?: string;
}

export const Truncate = forwardRef<HTMLSpanElement, TruncateProps>(function Truncate(
  { lines = 1, title, className, children, style, ...rest },
  ref,
) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [measured, setMeasured] = useState<string>('');

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const check = () => {
      const cut =
        lines > 1
          ? node.scrollHeight > node.clientHeight + 1
          : node.scrollWidth > node.clientWidth + 1;
      setOverflowing(cut);
      setMeasured(node.textContent ?? '');
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [lines, children]);

  return (
    <span
      ref={composeRefs(ref, nodeRef)}
      title={overflowing ? (title ?? measured) : undefined}
      className={cn(
        'min-w-0',
        lines > 1 ? 'overflow-hidden' : 'block truncate',
        className,
      )}
      style={
        lines > 1
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: lines,
              ...style,
            }
          : style
      }
      {...rest}
    >
      {children}
    </span>
  );
});
